import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Image,
  Camera,
  FileText,
  Check,
  X,
  Download,
  Save,
  Upload,
  LucideIcon,
  AlertTriangle,
  FileCode,
  Code,
  Film,
  Video,
  Layers,
  Volume2,
} from 'lucide-react';
import { useExportStore, useEditorStore, useTemplateStore, useAnimationStore } from '@/stores';
import { isAudioLayer } from '@/types/layers';
import { BatchExportPanel } from './BatchExportPanel';
import type { ExportFormat } from '@/types';
import {
  createSlateFile,
  readSlateFile,
  downloadSlateFile,
  generateSlateFilename,
  captureCanvasPreview,
  designToTemplate,
  SLATE_EXTENSION,
} from '@/lib/slate';
import {
  exportToSVG,
  downloadSVG,
  exportToImageVector,
  downloadImageVector,
  exportToGIF,
  downloadGIF,
  exportToWebM,
  downloadWebM,
} from '@/lib/export';

// Format definition with optional disabled flag
interface FormatOption {
  id: ExportFormat;
  name: string;
  ext: string;
  description: string;
  icon: LucideIcon;
  disabled?: boolean;
}

// Group formats by category
const FORMAT_CATEGORIES: { name: string; formats: FormatOption[] }[] = [
  {
    name: 'Raster',
    formats: [
      { id: 'png', name: 'PNG', ext: '.png', description: 'Lossless with transparency', icon: Image },
      { id: 'jpeg', name: 'JPEG', ext: '.jpg', description: 'Smaller file size', icon: Camera },
      { id: 'webp', name: 'WebP', ext: '.webp', description: 'Modern compression', icon: Image },
    ],
  },
  {
    name: 'Vector',
    formats: [
      { id: 'svg', name: 'SVG', ext: '.svg', description: 'Scalable vector graphics', icon: FileCode },
      { id: 'pdf', name: 'PDF', ext: '.pdf', description: 'Print-ready document', icon: FileText },
    ],
  },
  {
    name: 'Code',
    formats: [
      { id: 'imagevector', name: 'ImageVector', ext: '.kt', description: 'Kotlin Compose', icon: Code },
    ],
  },
  {
    name: 'Animation',
    formats: [
      { id: 'gif', name: 'GIF', ext: '.gif', description: 'Animated image', icon: Film },
      { id: 'webm', name: 'WebM', ext: '.webm', description: 'Video format', icon: Video },
    ],
  },
  {
    name: 'Design Tools',
    formats: [
      { id: 'figma', name: 'Figma', ext: '.fig', description: 'Coming soon', icon: Layers, disabled: true },
    ],
  },
];

// Flatten for lookup
const ALL_FORMATS = FORMAT_CATEGORIES.flatMap(c => c.formats);

export function ExportPanel() {
  const { selectedFormat, content } = useEditorStore();
  const { currentTemplate, variableValues, loadTemplate, setVariableValues } = useTemplateStore();
  const { tasks, createTask, updateTaskProgress, completeTask, failTask, clearCompletedTasks } =
    useExportStore();
  const { timeline, hasAnimations, duration: animationDuration, seek, stop } = useAnimationStore();

  // Get audio layers from template
  const audioLayers = useMemo(() => {
    if (!currentTemplate) return [];
    return currentTemplate.layers.filter(isAudioLayer);
  }, [currentTemplate]);

  const hasAudio = audioLayers.length > 0;

  // Check for content that may not export perfectly
  const exportWarnings = useMemo(() => {
    const warnings: string[] = [];

    // Only warn about animations for non-GIF formats
    if (hasAnimations()) {
      warnings.push('Animations will be captured at current frame');
    }

    if (currentTemplate?.layers) {
      const hasLottie = currentTemplate.layers.some(l => l.type === 'lottie');
      const hasRive = currentTemplate.layers.some(l => l.type === 'rive');

      if (hasLottie) {
        warnings.push('Lottie animations will be captured at current frame');
      }
      if (hasRive) {
        warnings.push('Rive animations will be captured at current frame');
      }
    }

    return warnings;
  }, [currentTemplate, hasAnimations]);

  const [selectedExportFormat, setSelectedExportFormat] = useState<ExportFormat>('png');
  const [scale, setScale] = useState(2);
  const [quality, setQuality] = useState(0.92);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // GIF-specific settings
  const [gifFps, setGifFps] = useState(15);
  const [gifDuration, setGifDuration] = useState(animationDuration || 3000);
  const [gifLoop, setGifLoop] = useState(true);
  const [gifQuality, setGifQuality] = useState(10);

  // WebM-specific settings
  const [webmFps, setWebmFps] = useState(30);
  const [webmDuration, setWebmDuration] = useState(animationDuration || 3000);
  const [webmQuality, setWebmQuality] = useState(0.95);

  const handleExport = useCallback(
    async () => {
      if (exporting || !currentTemplate) return;
      setExporting(true);
      const task = createTask('current', selectedExportFormat);

      try {
        const templateName = currentTemplate.metadata.name.toLowerCase().replace(/\s+/g, '-') || 'design';
        let filename = `mooncloak-${selectedFormat.id}-${templateName}`;

        // Handle vector/code formats differently
        if (selectedExportFormat === 'svg') {
          updateTaskProgress(task.id, 50);

          const svgString = exportToSVG(
            currentTemplate.layers,
            selectedFormat,
            currentTemplate.canvas.background,
            variableValues
          );

          updateTaskProgress(task.id, 90);
          downloadSVG(svgString, filename);

          const blob = new Blob([svgString], { type: 'image/svg+xml' });
          completeTask(task.id, blob);
          setExporting(false);
          return;
        }

        if (selectedExportFormat === 'imagevector') {
          updateTaskProgress(task.id, 50);

          const ktCode = exportToImageVector(
            currentTemplate.layers,
            selectedFormat,
            currentTemplate.canvas.background,
            variableValues,
            templateName
          );

          updateTaskProgress(task.id, 90);
          downloadImageVector(ktCode, filename);

          const blob = new Blob([ktCode], { type: 'text/plain' });
          completeTask(task.id, blob);
          setExporting(false);
          return;
        }

        // GIF export
        if (selectedExportFormat === 'gif') {
          const canvas = document.getElementById('ad-canvas');
          if (!canvas) throw new Error('Canvas not found');

          // Stop any playing animation
          stop();

          const blob = await exportToGIF(canvas, seek, {
            fps: gifFps,
            quality: gifQuality,
            scale: scale,
            duration: gifDuration,
            loop: gifLoop,
            onProgress: (progress) => {
              updateTaskProgress(task.id, Math.round(progress * 100));
            },
          });

          filename += '.gif';
          downloadGIF(blob, filename);
          completeTask(task.id, blob);
          setExporting(false);
          return;
        }

        // WebM export
        if (selectedExportFormat === 'webm') {
          const canvas = document.getElementById('ad-canvas');
          if (!canvas) throw new Error('Canvas not found');

          // Stop any playing animation
          stop();

          const blob = await exportToWebM(canvas, seek, {
            fps: webmFps,
            quality: webmQuality,
            scale: scale,
            duration: webmDuration,
            audioLayers: audioLayers.filter(l => l.visible), // Include visible audio layers
            onProgress: (progress) => {
              updateTaskProgress(task.id, Math.round(progress * 100));
            },
          });

          filename += '.webm';
          downloadWebM(blob, filename);
          completeTask(task.id, blob);
          setExporting(false);
          return;
        }

        // Raster formats use html2canvas
        const canvas = document.getElementById('ad-canvas');
        if (!canvas) throw new Error('Canvas not found');

        updateTaskProgress(task.id, 10);

        const html2canvas = (await import('html2canvas')).default;

        updateTaskProgress(task.id, 30);

        const capturedCanvas = await html2canvas(canvas, {
          scale,
          backgroundColor: null,
          logging: false,
          useCORS: true,
        });

        updateTaskProgress(task.id, 70);

        let blob: Blob;

        if (selectedExportFormat === 'png') {
          blob = await new Promise<Blob>((resolve) =>
            capturedCanvas.toBlob((b) => resolve(b!), 'image/png')
          );
          filename += '.png';
        } else if (selectedExportFormat === 'jpeg') {
          blob = await new Promise<Blob>((resolve) =>
            capturedCanvas.toBlob((b) => resolve(b!), 'image/jpeg', quality)
          );
          filename += '.jpg';
        } else if (selectedExportFormat === 'webp') {
          blob = await new Promise<Blob>((resolve) =>
            capturedCanvas.toBlob((b) => resolve(b!), 'image/webp', quality)
          );
          filename += '.webp';
        } else if (selectedExportFormat === 'pdf') {
          const { jsPDF } = await import('jspdf');
          const pdf = new jsPDF({
            orientation: selectedFormat.width > selectedFormat.height ? 'landscape' : 'portrait',
            unit: 'px',
            format: [selectedFormat.width * scale, selectedFormat.height * scale],
          });
          const imgData = capturedCanvas.toDataURL('image/png');
          pdf.addImage(imgData, 'PNG', 0, 0, selectedFormat.width * scale, selectedFormat.height * scale);
          blob = pdf.output('blob');
          filename += '.pdf';
        } else {
          throw new Error('Unsupported format');
        }

        updateTaskProgress(task.id, 90);

        // Download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        completeTask(task.id, blob);
      } catch (error) {
        failTask(task.id, (error as Error).message);
      } finally {
        setExporting(false);
      }
    },
    [
      selectedFormat,
      currentTemplate,
      variableValues,
      selectedExportFormat,
      scale,
      quality,
      gifFps,
      gifDuration,
      gifLoop,
      gifQuality,
      webmFps,
      webmDuration,
      webmQuality,
      audioLayers,
      exporting,
      createTask,
      updateTaskProgress,
      completeTask,
      failTask,
      seek,
      stop,
    ]
  );

  const handleSave = useCallback(async () => {
    if (saving || !currentTemplate) return;
    setSaving(true);

    try {
      const canvas = document.getElementById('ad-canvas');
      if (!canvas) throw new Error('Canvas not found');

      const previewBlob = await captureCanvasPreview(canvas, 1);

      const slateBlob = await createSlateFile(
        currentTemplate,
        selectedFormat,
        {
          headline: content.headline,
          subhead: content.subhead || '',
          cta: content.cta,
          ...variableValues,
        },
        previewBlob,
        {
          name: currentTemplate.metadata.name,
          description: currentTemplate.metadata.description,
        }
      );

      const filename = generateSlateFilename(
        currentTemplate.metadata.name,
        selectedFormat.id
      );
      downloadSlateFile(slateBlob, filename);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  }, [saving, currentTemplate, selectedFormat, content, variableValues]);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImport = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportError(null);

    try {
      const slateFile = await readSlateFile(file);
      const template = designToTemplate(slateFile.design, slateFile.manifest);
      loadTemplate(template);

      if (slateFile.design.variableValues) {
        setVariableValues(slateFile.design.variableValues);
      }
    } catch (error) {
      setImportError((error as Error).message);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [loadTemplate, setVariableValues]);

  const completedTasks = tasks.filter((t) => t.status === 'completed');
  const failedTasks = tasks.filter((t) => t.status === 'failed');
  const selectedFormatConfig = ALL_FORMATS.find(f => f.id === selectedExportFormat);

  // Format-specific warnings
  const formatWarnings = useMemo(() => {
    const warnings: string[] = [];

    if (selectedExportFormat === 'svg') {
      if (currentTemplate?.layers.some(l => l.type === 'image')) {
        warnings.push('Images will be embedded as links - ensure URLs are accessible');
      }
      if (currentTemplate?.layers.some(l => l.type === 'primitive')) {
        warnings.push('Primitives will be shown as placeholders');
      }
    }

    if (selectedExportFormat === 'imagevector') {
      warnings.push('Only shapes are fully supported - text and images noted as comments');
    }

    if (selectedExportFormat === 'gif') {
      if (!hasAnimations()) {
        warnings.push('No animations detected - GIF will show static frames');
      }
      if (hasAudio) {
        warnings.push('GIF format does not support audio - audio tracks will be ignored');
      }
    }

    if (selectedExportFormat === 'webm') {
      if (!hasAnimations()) {
        warnings.push('No animations detected - video will show static frames');
      }
      warnings.push('WebM requires a Chrome-based browser');
    }

    return warnings;
  }, [selectedExportFormat, currentTemplate, hasAnimations, hasAudio]);

  // Don't show animation warnings for GIF/WebM (they capture them)
  const allWarnings = selectedExportFormat === 'gif' || selectedExportFormat === 'webm'
    ? formatWarnings
    : [...exportWarnings, ...formatWarnings];

  return (
    <div className="grid grid-cols-[1fr_280px] gap-6">
      {/* Left column: Export formats and actions */}
      <div>
        <h3 className="label mb-3">Export Format</h3>

        {/* Format categories */}
        <div className="space-y-4 mb-4">
          {FORMAT_CATEGORIES.map((category) => (
            <div key={category.name}>
              <h4 className="text-[10px] uppercase tracking-wider text-fg-subtle mb-2">
                {category.name}
              </h4>
              <div className="flex flex-wrap gap-2">
                {category.formats.map((format) => (
                  <button
                    key={format.id}
                    onClick={() => !format.disabled && setSelectedExportFormat(format.id)}
                    disabled={format.disabled}
                    className={`
                      px-3 py-2 rounded-lg transition-colors flex items-center gap-2
                      ${format.disabled
                        ? 'opacity-40 cursor-not-allowed bg-white/5'
                        : selectedExportFormat === format.id
                          ? 'bg-primary/20 border border-primary text-primary'
                          : 'bg-white/5 border border-transparent hover:bg-white/10'
                      }
                    `}
                    title={format.description}
                  >
                    <format.icon size={14} />
                    <span className="text-sm">{format.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Format description */}
        {selectedFormatConfig && (
          <p className="text-xs text-fg-muted mb-4">
            {selectedFormatConfig.description}
          </p>
        )}

        {/* Export warnings */}
        {allWarnings.length > 0 && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-yellow-400 flex-none mt-0.5" />
              <div className="text-xs text-yellow-200 space-y-1">
                {allWarnings.map((warning, i) => (
                  <p key={i}>{warning}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Export button */}
        <button
          onClick={handleExport}
          disabled={exporting || selectedFormatConfig?.disabled}
          className="w-full btn-primary py-3 px-6 flex items-center justify-center gap-2 disabled:opacity-50 mb-4"
        >
          <Download size={18} />
          {exporting ? 'Exporting...' : `Export ${selectedFormatConfig?.name || ''}`}
        </button>

        {/* Batch export - only for supported formats */}
        {['png', 'jpeg', 'webp', 'svg', 'pdf'].includes(selectedExportFormat) && (
          <BatchExportPanel
            selectedExportFormat={selectedExportFormat}
            scale={scale}
            quality={quality}
          />
        )}

        {/* Project file section */}
        <div className="border-t border-white/10 pt-4 mt-4">
          <h3 className="label mb-3">Project File</h3>
          <p className="text-xs text-fg-subtle mb-3">
            Save as <code className="px-1 py-0.5 bg-white/10 rounded">{SLATE_EXTENSION}</code> to preserve layers,
            variables, and settings.
          </p>

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !currentTemplate}
              className="flex-1 py-2.5 px-4 bg-white/10 hover:bg-white/15 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleImportClick}
              disabled={importing}
              className="flex-1 py-2.5 px-4 bg-white/10 hover:bg-white/15 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              <Upload size={16} />
              {importing ? 'Opening...' : 'Open'}
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={SLATE_EXTENSION}
            onChange={handleImport}
            className="hidden"
          />

          {importError && (
            <div className="mt-3 p-2 bg-red-500/10 rounded-lg flex items-center gap-2 text-sm text-red-400">
              <X size={14} />
              {importError}
            </div>
          )}
        </div>

        {/* Recent exports */}
        {(completedTasks.length > 0 || failedTasks.length > 0) && (
          <div className="mt-4 border-t border-white/10 pt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="label mb-0">Recent Exports</h3>
              <button
                onClick={clearCompletedTasks}
                className="text-xs text-fg-subtle hover:text-fg"
              >
                Clear
              </button>
            </div>
            <div className="space-y-2">
              {completedTasks.slice(-3).map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 px-3 py-2 bg-green-500/10 rounded-lg"
                >
                  <Check size={14} className="text-green-400" />
                  <span className="text-sm">{task.format.toUpperCase()} exported</span>
                </div>
              ))}
              {failedTasks.slice(-3).map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 px-3 py-2 bg-red-500/10 rounded-lg"
                >
                  <X size={14} className="text-red-400" />
                  <span className="text-sm text-red-400">{task.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right column: Export settings */}
      <div className="space-y-4">
        {/* Scale (for raster formats including GIF/WebM) */}
        {['png', 'jpeg', 'webp', 'pdf', 'gif', 'webm'].includes(selectedExportFormat) && (
          <div>
            <label className="label">Scale</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={4}
                step={0.5}
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-medium w-10">{scale}x</span>
            </div>
            <p className="text-xs text-fg-subtle mt-1">
              Output: {selectedFormat.width * scale} × {selectedFormat.height * scale}px
            </p>
          </div>
        )}

        {/* Quality (for lossy formats) */}
        {['jpeg', 'webp'].includes(selectedExportFormat) && (
          <div>
            <label className="label">Quality</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0.5}
                max={1}
                step={0.01}
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-medium w-10">{Math.round(quality * 100)}%</span>
            </div>
          </div>
        )}

        {/* GIF settings */}
        {selectedExportFormat === 'gif' && (
          <>
            <div>
              <label className="label">Frame Rate</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={5}
                  max={30}
                  step={1}
                  value={gifFps}
                  onChange={(e) => setGifFps(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16">{gifFps} fps</span>
              </div>
            </div>

            <div>
              <label className="label">Duration</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={500}
                  max={10000}
                  step={100}
                  value={gifDuration}
                  onChange={(e) => setGifDuration(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16">{(gifDuration / 1000).toFixed(1)}s</span>
              </div>
              {animationDuration > 0 && (
                <button
                  onClick={() => setGifDuration(animationDuration)}
                  className="text-xs text-primary hover:text-primary/80 mt-1"
                >
                  Use animation duration ({(animationDuration / 1000).toFixed(1)}s)
                </button>
              )}
            </div>

            <div>
              <label className="label">GIF Quality</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={30}
                  step={1}
                  value={gifQuality}
                  onChange={(e) => setGifQuality(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16">
                  {gifQuality <= 10 ? 'High' : gifQuality <= 20 ? 'Medium' : 'Low'}
                </span>
              </div>
              <p className="text-xs text-fg-subtle mt-1">
                Lower values = better quality, larger file
              </p>
            </div>

            <div className="flex items-center justify-between">
              <label className="label mb-0">Loop Animation</label>
              <button
                onClick={() => setGifLoop(!gifLoop)}
                className={`
                  w-10 h-5 rounded-full transition-colors relative
                  ${gifLoop ? 'bg-primary' : 'bg-white/20'}
                `}
              >
                <span
                  className={`
                    absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform
                    ${gifLoop ? 'left-5' : 'left-0.5'}
                  `}
                />
              </button>
            </div>

            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-xs text-fg-subtle">
                Estimated frames: {Math.ceil((gifDuration / 1000) * gifFps)}
              </p>
            </div>
          </>
        )}

        {/* WebM settings */}
        {selectedExportFormat === 'webm' && (
          <>
            <div>
              <label className="label">Frame Rate</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={15}
                  max={60}
                  step={1}
                  value={webmFps}
                  onChange={(e) => setWebmFps(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16">{webmFps} fps</span>
              </div>
            </div>

            <div>
              <label className="label">Duration</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={500}
                  max={30000}
                  step={100}
                  value={webmDuration}
                  onChange={(e) => setWebmDuration(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16">{(webmDuration / 1000).toFixed(1)}s</span>
              </div>
              {animationDuration > 0 && (
                <button
                  onClick={() => setWebmDuration(animationDuration)}
                  className="text-xs text-primary hover:text-primary/80 mt-1"
                >
                  Use animation duration ({(animationDuration / 1000).toFixed(1)}s)
                </button>
              )}
            </div>

            <div>
              <label className="label">Video Quality</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0.5}
                  max={0.99}
                  step={0.01}
                  value={webmQuality}
                  onChange={(e) => setWebmQuality(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16">{Math.round(webmQuality * 100)}%</span>
              </div>
            </div>

            <div className="p-3 bg-white/5 rounded-lg">
              <h4 className="text-xs font-medium text-fg mb-2">WebM Video</h4>
              <p className="text-xs text-fg-subtle">
                Creates a VP8/VP9 video. Best for web playback.
                Estimated frames: {Math.ceil((webmDuration / 1000) * webmFps)}
              </p>
            </div>
          </>
        )}

        {/* SVG info */}
        {selectedExportFormat === 'svg' && (
          <div className="p-3 bg-white/5 rounded-lg">
            <h4 className="text-xs font-medium text-fg mb-2">SVG Export</h4>
            <p className="text-xs text-fg-subtle">
              Exports layers as scalable vector graphics. Best for shapes and text.
              Complex primitives may be simplified.
            </p>
          </div>
        )}

        {/* ImageVector info */}
        {selectedExportFormat === 'imagevector' && (
          <div className="p-3 bg-white/5 rounded-lg">
            <h4 className="text-xs font-medium text-fg mb-2">Compose ImageVector</h4>
            <p className="text-xs text-fg-subtle">
              Generates Kotlin code for Jetpack Compose / Compose Multiplatform.
              Ideal for icons and simple graphics.
            </p>
          </div>
        )}

        <div className="pt-4 border-t border-white/10">
          <h3 className="label mb-2">Current Design</h3>
          <div className="space-y-1 text-xs text-fg-subtle">
            <p>
              <span className="text-fg-muted">Format:</span> {selectedFormat.name}
            </p>
            <p>
              <span className="text-fg-muted">Template:</span> {currentTemplate?.metadata.name || 'None'}
            </p>
            <p>
              <span className="text-fg-muted">Size:</span> {selectedFormat.width} × {selectedFormat.height}
            </p>
            <p>
              <span className="text-fg-muted">Layers:</span> {currentTemplate?.layers.length || 0}
            </p>
            {hasAnimations() && (
              <p>
                <span className="text-fg-muted">Animation:</span> {(animationDuration / 1000).toFixed(1)}s
              </p>
            )}
            {hasAudio && (
              <p className="flex items-center gap-1">
                <Volume2 size={12} className="text-fg-muted" />
                <span className="text-fg-muted">Audio:</span> {audioLayers.length} track{audioLayers.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
